import { useMemo, useState } from "react";
import { Link, useLocation } from "wouter";
import { useQuery as useConvexQuery, useConvexAuth } from "convex/react";
import { api } from "../../../convex/_generated/api";
import ScreenHeader from "@/components/mnf/ScreenHeader";
import { GlassCard, PillTag, Btn, Avatar, SectionTitle } from "@/components/mnf/UI";
import Icon from "@/components/mnf/Icon";
import {
  useStoreQuery,
  useStoreMutation,
  queries,
  mutations,
  tierAtLeast,
  type LiveClass,
} from "@/lib/store";
import { LockOverlay, UpsellCard } from "@/components/mnf/TierLock";
import CommunityTab from "@/components/mnf/CommunityTab";
import { formatTime } from "@/lib/datetime";

/**
 * Gather.
 *
 * Live / Schedule / Replays still read the in-memory store (client/src/lib/store.ts) —
 * convex/live.ts exists but is not wired, and that is a separate migration.
 * The COMMUNITY tab is Convex-backed and genuinely shared between members; it
 * lives in components/mnf/CommunityTab.tsx.
 */

type GatherTab = "live" | "schedule" | "replays" | "community";

const CATEGORY_ICON: Record<string, string> = {
  strength: "fitness_center",
  yoga: "self_improvement",
  prayer: "menu_book",
  teaching: "auto_stories",
  cardio: "directions_run",
  recovery: "spa",
};

function relativeWhen(ts: number) {
  const diff = ts - Date.now();
  const mins = Math.round(diff / 60000);
  if (mins < 0) return "Started";
  if (mins < 60) return `In ${mins}m`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `In ${hrs}h`;
  const days = Math.round(hrs / 24);
  return `In ${days}d`;
}

function dayBucket(ts: number): { key: string; label: string; sub: string } {
  const d = new Date(ts);
  const now = new Date();
  const tomorrow = new Date(now); tomorrow.setDate(now.getDate() + 1);
  const sameDay = d.toDateString() === now.toDateString();
  const isTomorrow = d.toDateString() === tomorrow.toDateString();
  const dayKey = d.toDateString();
  if (sameDay) return { key: dayKey, label: "Today", sub: d.toLocaleDateString([], { weekday: "long", month: "short", day: "numeric" }) };
  if (isTomorrow) return { key: dayKey, label: "Tomorrow", sub: d.toLocaleDateString([], { weekday: "long", month: "short", day: "numeric" }) };
  return {
    key: dayKey,
    label: d.toLocaleDateString([], { weekday: "long" }),
    sub: d.toLocaleDateString([], { month: "short", day: "numeric" }),
  };
}

export default function Gather() {
  const me = useStoreQuery(queries.currentUser);
  const [, setLocation] = useLocation();
  const liveNow = useStoreQuery(queries.liveNow);
  const upcoming = useStoreQuery(queries.liveUpcoming);
  const replays = useStoreQuery(queries.liveReplays);
  const canHostMeetup = tierAtLeast(me?.tier, "Rooted");
  const replayPreviewCount = me?.tier === "Seedling" ? 2 : replays.length;

  const [tab, setTab] = useState<GatherTab>("live");

  // Header count only. Same query + args as CommunityTab's, so the Convex
  // client shares one subscription rather than opening a second.
  const { isAuthenticated } = useConvexAuth();
  const feedCount = useConvexQuery(api.community.feedRecent, isAuthenticated ? {} : "skip")?.length;

  const toggleReminder = useStoreMutation(mutations.toggleClassReminder);

  const live = liveNow[0];
  const tonight = upcoming[0];
  const cityLine = me?.city ? `Within reach of ${me.city}` : "Add a zip in onboarding to see what's near you";

  return (
    <div className="pb-6">
      <ScreenHeader
        variant="forest"
        eyebrow="Gather"
        title={<>Live &amp; <span className="italic">community</span></>}
        subtitle={<>{cityLine}<span style={{ opacity: 0.65 }}> · {me?.zipCode ?? "set zip"}</span></>}
        trailing={
          <Link href="/go-live" data-testid="link-go-live" className="no-underline">
            <span
              className="inline-flex items-center gap-1.5 mnf-body"
              style={{
                padding: "7px 13px", borderRadius: 999,
                background: "var(--mnf-t)", color: "#fff",
                fontSize: 10.5, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase",
                border: "1px solid var(--mnf-t)",
              }}
            >
              <Icon name="videocam" size={14} weight={500} /> Go Live
            </span>
          </Link>
        }
      >
        <div className="flex items-center gap-2 flex-wrap">
          <PillTag tone={live ? "coral" : "fiber"}>
            {live ? "● Live now" : "Quiet right now"}
          </PillTag>
          <PillTag tone="neutral">{upcoming.length} scheduled</PillTag>
          <PillTag tone="purple">
            {feedCount === undefined ? "Sister voices" : `${feedCount} sister voices`}
          </PillTag>
        </div>

        {/* Tab pills */}
        <div
          className="flex items-center mt-4 p-1 mnf-body"
          style={{
            background: "rgba(245,240,232,0.10)",
            border: "1px solid rgba(245,240,232,0.18)",
            borderRadius: 999,
            backdropFilter: "blur(8px)",
          }}
        >
          {([
            { k: "live",      l: "Live" },
            { k: "schedule",  l: "Schedule" },
            { k: "replays",   l: "Replays" },
            { k: "community", l: "Community" },
          ] as { k: GatherTab; l: string }[]).map((t) => {
            const active = tab === t.k;
            return (
              <button
                key={t.k}
                onClick={() => setTab(t.k)}
                data-testid={`gather-tab-${t.k}`}
                className="flex-1 transition-all"
                style={{
                  height: 34,
                  borderRadius: 999,
                  background: active ? "#F5F0E8" : "transparent",
                  color: active ? "var(--mnf-f)" : "rgba(245,240,232,0.85)",
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.05em",
                  textTransform: "uppercase",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                {t.l}
              </button>
            );
          })}
        </div>
      </ScreenHeader>

      <div className="px-5 -mt-3 space-y-5">
        {tab === "live" && (
          <LiveTab
            me={me}
            live={live}
            tonight={tonight}
            onJoinLocked={(id) => setLocation(`/live/${id}`)}
            // Guarded so the write is unreachable while the identity is unresolved;
            // never toggles a reminder against the seeded demo account.
            onToggleReminder={(classId) => { if (me?.id) toggleReminder(me.id, classId); }}
          />
        )}

        {tab === "schedule" && (
          <ScheduleTab
            me={me}
            upcoming={upcoming}
            onJoinLocked={(id) => setLocation(`/live/${id}`)}
            // Guarded so the write is unreachable while the identity is unresolved;
            // never toggles a reminder against the seeded demo account.
            onToggleReminder={(classId) => { if (me?.id) toggleReminder(me.id, classId); }}
          />
        )}

        {tab === "replays" && (
          <ReplaysTab
            me={me}
            replays={replays}
            replayPreviewCount={replayPreviewCount}
            onLockTap={(id) => setLocation(`/live/${id}`)}
            onSeeRooted={() => setLocation("/garden")}
          />
        )}

        {tab === "community" && (
          <CommunityTab
            me={me}
            canHostMeetup={canHostMeetup}
            onSeeRooted={() => setLocation("/garden")}
          />
        )}
      </div>
    </div>
  );
}

/* ================================ LIVE TAB ================================ */

function LiveTab({
  me, live, tonight, onJoinLocked, onToggleReminder,
}: {
  me: ReturnType<typeof queries.currentUser>;
  live: LiveClass | undefined;
  tonight: LiveClass | undefined;
  onJoinLocked: (id: string) => void;
  onToggleReminder: (classId: string) => void;
}) {
  const hasReminder = useStoreQuery(
    useMemo(
      () => () => (tonight && me?.id ? queries.hasReminder(me.id, tonight.id) : false),
      [me?.id, tonight?.id],
    ),
  );

  return (
    <>
      {/* Live now */}
      {live ? (
        tierAtLeast(me?.tier, live.tierMin) ? (
          <LiveNowCard cls={live} />
        ) : (
          <LockOverlay
            tier={live.tierMin}
            onTap={() => onJoinLocked(live.id)}
            testid={`lock-live-${live.id}`}
          >
            <LiveNowCard cls={live} />
          </LockOverlay>
        )
      ) : (
        <QuietNowCard tonight={tonight} />
      )}

      {/* Tonight hero */}
      {tonight && (!live || tonight.id !== live.id) && (
        <TonightHero
          cls={tonight}
          locked={!tierAtLeast(me?.tier, tonight.tierMin)}
          hasReminder={hasReminder}
          onToggleReminder={() => onToggleReminder(tonight.id)}
          onLockTap={() => onJoinLocked(tonight.id)}
        />
      )}

      {/* What's gathering */}
      <GlassCard padding={18} className="mnf-fade-up">
        <p className="mnf-eyebrow" style={{ color: "var(--mnf-t)" }}>What's gathering</p>
        <p className="mnf-serif mt-1" style={{ fontSize: 18, fontWeight: 500, color: "var(--mnf-fd)", lineHeight: 1.3 }}>
          A live, breathing rhythm of strength, prayer, and Scripture, hosted by Coach Kiley.
        </p>
        <div className="grid grid-cols-3 gap-2 mt-4">
          <StatBlock label="This week" value="6" sub="classes" />
          <StatBlock label="Tonight" value={tonight ? relativeWhen(tonight.scheduledFor) : "Quiet"} sub={tonight ? "until live" : "no class"} />
          <StatBlock label="Sisters in" value={live ? `${live.watching}` : "-"} sub="watching now" />
        </div>
      </GlassCard>
    </>
  );
}

function QuietNowCard({ tonight }: { tonight: LiveClass | undefined }) {
  return (
    <GlassCard
      organic
      padding={20}
      className="mnf-fade-up"
      style={{
        background: "linear-gradient(135deg, rgba(168,212,184,0.18) 0%, rgba(245,240,232,0.7) 100%)",
        border: "1px solid rgba(30,74,53,0.10)",
      }}
    >
      <div className="flex items-start gap-3">
        <div
          className="flex items-center justify-center shrink-0"
          style={{
            width: 52, height: 52,
            borderRadius: "18px 6px 18px 6px",
            background: "rgba(30,74,53,0.08)", color: "var(--mnf-f)",
          }}
        >
          <Icon name="self_improvement" size={26} weight={400} />
        </div>
        <div className="flex-1">
          <p className="mnf-eyebrow" style={{ color: "var(--mnf-t)" }}>Quiet right now</p>
          <p className="mnf-serif mt-1" style={{ fontSize: 18, fontWeight: 500, color: "var(--mnf-fd)", lineHeight: 1.3 }}>
            No class is live. Rest is also a rhythm.
          </p>
          {tonight && (
            <p className="mnf-body mt-2" style={{ fontSize: 12.5, color: "var(--mnf-tg)", lineHeight: 1.45 }}>
              Next up: <span style={{ color: "var(--mnf-fd)", fontWeight: 600 }}>{tonight.title}</span>, {relativeWhen(tonight.scheduledFor).toLowerCase()}.
            </p>
          )}
        </div>
      </div>
    </GlassCard>
  );
}

function TonightHero({
  cls, locked, hasReminder, onToggleReminder, onLockTap,
}: {
  cls: LiveClass;
  locked: boolean;
  hasReminder: boolean;
  onToggleReminder: () => void;
  onLockTap: () => void;
}) {
  const icon = CATEGORY_ICON[cls.category] ?? "videocam";
  const inner = (
    <GlassCard
      padding={0}
      className="mnf-fade-up"
      style={{ overflow: "hidden", border: "1px solid rgba(127,119,221,0.30)" }}
    >
      <div
        className="p-4"
        style={{
          background: "linear-gradient(135deg, rgba(127,119,221,0.16) 0%, rgba(245,240,232,0.6) 100%)",
        }}
      >
        <div className="flex items-center gap-3">
          <div
            className="flex items-center justify-center shrink-0"
            style={{
              width: 48, height: 48,
              borderRadius: "16px 6px 16px 6px",
              background: "var(--mnf-purple)", color: "#fff",
            }}
          >
            <Icon name={icon} size={22} weight={500} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <p className="mnf-eyebrow" style={{ color: "var(--mnf-purple)" }}>
                Tonight's class
              </p>
              <PillTag tone="purple">{relativeWhen(cls.scheduledFor)}</PillTag>
            </div>
            <h3
              className="mnf-serif mt-1 truncate"
              style={{ fontSize: 18, fontWeight: 500, color: "var(--mnf-fd)", lineHeight: 1.25 }}
            >
              {cls.title}
            </h3>
            <p className="mnf-body" style={{ fontSize: 11.5, color: "var(--mnf-tg)", marginTop: 2 }}>
              {formatTime(cls.scheduledFor)} · {cls.durationMin} min · {cls.tierMin}+
            </p>
          </div>
        </div>
        <p className="mnf-body italic mt-3" style={{ fontSize: 12.5, color: "var(--mnf-tg)", lineHeight: 1.5 }}>
          {cls.description}
        </p>
        <div className="flex items-center gap-2 mt-4">
          <Btn
            variant={hasReminder ? "secondary" : "primary"}
            size="sm"
            block
            onClick={(e: React.MouseEvent) => {
              e.preventDefault();
              e.stopPropagation();
              onToggleReminder();
            }}
            data-testid={`button-remind-${cls.id}`}
          >
            <Icon name={hasReminder ? "notifications_active" : "notifications"} size={14} />
            {hasReminder ? "Reminder set" : "Remind me"}
          </Btn>
          <Link href={`/live/${cls.id}`} className="no-underline" style={{ flex: 1 }} data-testid={`link-tonight-${cls.id}`}>
            <Btn variant="ghost" size="sm" block>
              <Icon name="info" size={14} /> Details
            </Btn>
          </Link>
        </div>
      </div>
    </GlassCard>
  );
  if (locked) {
    return (
      <LockOverlay tier={cls.tierMin} onTap={onLockTap} testid={`lock-tonight-${cls.id}`}>
        {inner}
      </LockOverlay>
    );
  }
  return inner;
}

function StatBlock({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <div
      className="p-3"
      style={{
        background: "rgba(255,255,255,0.55)",
        border: "1px solid rgba(30,74,53,0.06)",
        borderRadius: 16,
      }}
    >
      <p className="text-[9px] mnf-body uppercase" style={{ color: "var(--mnf-tg)", letterSpacing: "0.12em", fontWeight: 700 }}>
        {label}
      </p>
      <p
        className="mnf-serif mt-1"
        style={{ fontSize: 18, fontWeight: 500, color: "var(--mnf-fd)", lineHeight: 1.05 }}
      >
        {value}
      </p>
      <p className="text-[10px] mnf-body" style={{ color: "var(--mnf-tg)", marginTop: 2 }}>
        {sub}
      </p>
    </div>
  );
}

/* ============================== SCHEDULE TAB ============================== */

function ScheduleTab({
  me, upcoming, onJoinLocked, onToggleReminder,
}: {
  me: ReturnType<typeof queries.currentUser>;
  upcoming: LiveClass[];
  onJoinLocked: (id: string) => void;
  onToggleReminder: (classId: string) => void;
}) {
  // Group classes by day
  const grouped = useMemo(() => {
    const map = new Map<string, { label: string; sub: string; ts: number; classes: LiveClass[] }>();
    upcoming.forEach((c) => {
      const b = dayBucket(c.scheduledFor);
      if (!map.has(b.key)) {
        map.set(b.key, { label: b.label, sub: b.sub, ts: c.scheduledFor, classes: [] });
      }
      map.get(b.key)!.classes.push(c);
    });
    return Array.from(map.values()).sort((a, b) => a.ts - b.ts);
  }, [upcoming]);

  const reminders = useStoreQuery(
    useMemo(() => () => (me?.id ? queries.classReminders(me.id) : []), [me?.id]),
  );
  const reminderSet = new Set(reminders.map((r) => r.classId));

  if (upcoming.length === 0) {
    return (
      <GlassCard padding={20}>
        <p className="mnf-body italic text-center" style={{ fontSize: 13, color: "var(--mnf-tg)" }}>
          No classes on the schedule. Coach Kiley is resting too.
        </p>
      </GlassCard>
    );
  }

  return (
    <>
      <SectionTitle
        eyebrow="On the schedule"
        title="The week ahead"
        trailing={<span>{upcoming.length} classes</span>}
      />

      <div className="space-y-4 mt-3">
        {grouped.map((day) => (
          <div key={day.label + day.sub}>
            <div className="flex items-baseline justify-between mb-2 px-1">
              <p
                className="mnf-serif"
                style={{ fontSize: 16, fontWeight: 500, color: "var(--mnf-fd)", lineHeight: 1.1 }}
              >
                {day.label}
              </p>
              <p className="text-[11px] mnf-body uppercase" style={{ color: "var(--mnf-tg)", letterSpacing: "0.1em" }}>
                {day.sub}
              </p>
            </div>
            <div className="space-y-2">
              {day.classes.map((c) => (
                <ScheduleRow
                  key={c.id}
                  cls={c}
                  locked={!tierAtLeast(me?.tier, c.tierMin)}
                  hasReminder={reminderSet.has(c.id)}
                  onLockTap={() => onJoinLocked(c.id)}
                  onToggleReminder={() => onToggleReminder(c.id)}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

function ScheduleRow({
  cls, locked, hasReminder, onLockTap, onToggleReminder,
}: {
  cls: LiveClass;
  locked: boolean;
  hasReminder: boolean;
  onLockTap: () => void;
  onToggleReminder: () => void;
}) {
  const icon = CATEGORY_ICON[cls.category] ?? "videocam";
  const time = new Date(cls.scheduledFor).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  const inner = (
    <GlassCard padding={14} data-testid={`schedule-row-${cls.id}`}>
      <div className="flex items-center gap-3">
        <div
          className="flex flex-col items-center justify-center shrink-0"
          style={{
            width: 56, height: 56,
            borderRadius: "16px 6px 16px 6px",
            background: "rgba(30,74,53,0.06)", color: "var(--mnf-f)",
          }}
        >
          <span className="mnf-serif" style={{ fontSize: 14, fontWeight: 600, lineHeight: 1, color: "var(--mnf-fd)" }}>
            {time.replace(/\s/g, "")}
          </span>
          <Icon name={icon} size={14} color="var(--mnf-t)" />
        </div>
        <div className="flex-1 min-w-0">
          <h4
            className="mnf-serif truncate"
            style={{ fontSize: 15, fontWeight: 500, color: "var(--mnf-fd)", lineHeight: 1.2 }}
          >
            {cls.title}
          </h4>
          <p className="mnf-body" style={{ fontSize: 11.5, color: "var(--mnf-tg)", marginTop: 2, lineHeight: 1.35 }}>
            {cls.durationMin} min · {cls.tierMin}+
          </p>
          <div className="flex items-center gap-1.5 mt-1.5">
            <PillTag tone="neutral">{relativeWhen(cls.scheduledFor)}</PillTag>
            {cls.verseRef && (
              <span className="mnf-body italic text-[10.5px]" style={{ color: "var(--mnf-tg)" }}>
                {cls.verseRef}
              </span>
            )}
          </div>
        </div>
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onToggleReminder();
          }}
          data-testid={`button-remind-${cls.id}`}
          aria-label={hasReminder ? "Cancel reminder" : "Set reminder"}
          className="flex items-center justify-center transition-all hover-elevate shrink-0"
          style={{
            width: 38, height: 38, borderRadius: 999,
            background: hasReminder ? "var(--mnf-t)" : "rgba(255,255,255,0.7)",
            color: hasReminder ? "#fff" : "var(--mnf-f)",
            border: hasReminder ? "1px solid var(--mnf-t)" : "1px solid rgba(30,74,53,0.10)",
            cursor: "pointer",
          }}
        >
          <Icon name={hasReminder ? "notifications_active" : "notifications"} size={16} />
        </button>
      </div>
    </GlassCard>
  );
  if (locked) {
    return (
      <LockOverlay tier={cls.tierMin} onTap={onLockTap} testid={`lock-schedule-${cls.id}`}>
        {inner}
      </LockOverlay>
    );
  }
  return (
    <Link href={`/live/${cls.id}`} className="no-underline block" data-testid={`link-schedule-${cls.id}`}>
      {inner}
    </Link>
  );
}

/* ============================== REPLAYS TAB =============================== */

function ReplaysTab({
  me, replays, replayPreviewCount, onLockTap, onSeeRooted,
}: {
  me: ReturnType<typeof queries.currentUser>;
  replays: LiveClass[];
  replayPreviewCount: number;
  onLockTap: (id: string) => void;
  onSeeRooted: () => void;
}) {
  if (replays.length === 0) {
    return (
      <GlassCard padding={20}>
        <p className="mnf-body italic text-center" style={{ fontSize: 13, color: "var(--mnf-tg)" }}>
          No replays yet. Catch the next live class to start the library.
        </p>
      </GlassCard>
    );
  }

  return (
    <>
      <SectionTitle
        eyebrow="On demand"
        title="Replay library"
        trailing={<span>{replays.length} sessions</span>}
      />
      <div className="grid grid-cols-2 gap-3 mt-3">
        {replays.slice(0, replayPreviewCount).map((c) => (
          tierAtLeast(me?.tier, c.tierMin) ? (
            <ReplayCard key={c.id} cls={c} />
          ) : (
            <LockOverlay
              key={c.id}
              tier={c.tierMin}
              onTap={() => onLockTap(c.id)}
              testid={`lock-replay-${c.id}`}
            >
              <ReplayCard cls={c} />
            </LockOverlay>
          )
        ))}
        {replays.slice(replayPreviewCount).map((c) => (
          <LockOverlay
            key={c.id}
            tier="Rooted"
            onTap={() => onLockTap(c.id)}
            testid={`lock-replay-extra-${c.id}`}
          >
            <ReplayCard cls={c} />
          </LockOverlay>
        ))}
      </div>
      {me?.tier === "Seedling" && replays.length > replayPreviewCount && (
        <div className="mt-3">
          <UpsellCard
            tier="Rooted"
            eyebrow="Replay library"
            title="Unlock the full library"
            description="Rooted sisters get every replay, plus on-demand strength and grace sessions you can revisit anytime."
            ctaLabel="See Rooted"
            onCta={onSeeRooted}
            icon="video_library"
          />
        </div>
      )}
    </>
  );
}


function LiveNowCard({ cls }: { cls: LiveClass }) {
  return (
    <Link href={`/live/${cls.id}`} className="no-underline block" data-testid={`link-live-${cls.id}`}>
      <GlassCard
        organic
        padding={0}
        style={{ overflow: "hidden", border: "1px solid rgba(192,120,74,0.45)" }}
      >
        <div
          className="relative p-5"
          style={{
            background:
              "linear-gradient(135deg, rgba(192,120,74,0.18) 0%, rgba(30,74,53,0.92) 100%)",
            color: "#F5F0E8",
          }}
        >
          <div className="flex items-center justify-between">
            <span className="inline-flex items-center gap-2 mnf-body" style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>
              <span className="live-dot" style={{ width: 8, height: 8, borderRadius: 999, background: "#FF5A5A", boxShadow: "0 0 0 4px rgba(255,90,90,0.25)" }} />
              Live now
            </span>
            <span className="mnf-body" style={{ fontSize: 11, fontWeight: 600, opacity: 0.9 }}>
              <Icon name="visibility" size={12} /> {cls.watching} watching
            </span>
          </div>
          <h3 className="mnf-serif mt-3" style={{ fontSize: 24, fontWeight: 500, lineHeight: 1.15 }}>
            {cls.title}
          </h3>
          <p className="mnf-body italic mt-1" style={{ fontSize: 13, opacity: 0.85, lineHeight: 1.45 }}>
            {cls.description}
          </p>
          <div className="flex items-center gap-3 mt-4">
            <Avatar initials="K" size={32} ringColor="rgba(245,240,232,0.6)" />
            <div className="flex-1">
              <p className="mnf-body" style={{ fontSize: 12, fontWeight: 700 }}>Coach Kiley</p>
              {cls.verseRef && (
                <p className="mnf-body italic" style={{ fontSize: 11, opacity: 0.8 }}>{cls.verseRef}</p>
              )}
            </div>
            <span
              className="inline-flex items-center gap-1.5 mnf-body"
              style={{
                padding: "10px 16px", borderRadius: 999,
                background: "#F5F0E8", color: "var(--mnf-fd)",
                fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase",
              }}
            >
              <Icon name="play_arrow" size={14} weight={600} /> Join
            </span>
          </div>
        </div>
      </GlassCard>
    </Link>
  );
}

function ReplayCard({ cls }: { cls: LiveClass }) {
  const icon = CATEGORY_ICON[cls.category] ?? "play_circle";
  return (
    <Link href={`/live/${cls.id}`} className="no-underline" data-testid={`link-replay-${cls.id}`}>
      <GlassCard padding={14} style={{ height: "100%" }}>
        <div
          className="flex items-center justify-center mb-3"
          style={{
            aspectRatio: "16/10",
            borderRadius: 14,
            background: "linear-gradient(135deg, rgba(168,212,184,0.4) 0%, rgba(30,74,53,0.85) 100%)",
            color: "#F5F0E8",
            position: "relative",
          }}
        >
          <Icon name={icon} size={36} weight={400} />
          <span
            className="absolute"
            style={{
              top: 8, right: 8,
              padding: "3px 8px", borderRadius: 999,
              background: "rgba(0,0,0,0.45)", color: "#F5F0E8",
              fontSize: 10, fontWeight: 600, letterSpacing: "0.04em",
            }}
          >
            {cls.replayDurationMin ?? cls.durationMin}m
          </span>
        </div>
        <h4 className="mnf-serif" style={{ fontSize: 14, fontWeight: 500, color: "var(--mnf-fd)", lineHeight: 1.25 }}>
          {cls.title}
        </h4>
        <p className="mnf-body" style={{ fontSize: 10.5, color: "var(--mnf-tg)", marginTop: 3, textTransform: "capitalize" }}>
          {cls.category} · {cls.attendees} attended
        </p>
      </GlassCard>
    </Link>
  );
}

